import React, { useRef, useState, useEffect } from 'react';
import { Camera, MapPin, CircleAlert as AlertCircle, CircleCheck as CheckCircle, Loader } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import type { RegistrationData } from './types';

interface Props {
  slug: string;
  registrationId: string;
  onComplete: (data: Partial<RegistrationData>) => void;
}

type Step = 'intro' | 'requesting' | 'camera' | 'blocked';

export default function PermissionStep({ slug, registrationId, onComplete }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const [step, setStep] = useState<Step>('intro');
  const [cameraGranted, setCameraGranted] = useState(false);
  const [locationGranted, setLocationGranted] = useState(false);
  const [location, setLocation] = useState<GeolocationCoordinates | null>(null);
  const [capturedSelfie, setCapturedSelfie] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [blockedReason, setBlockedReason] = useState('');

  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
      }
    };
  }, []);

  const requestPermissions = async () => {
    setStep('requesting');
    setError(null);

    let camGranted = false;
    let locGranted = false;
    let coords: GeolocationCoordinates | null = null;

    // Camera
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' }, audio: false });
      streamRef.current = stream;
      camGranted = true;
      setCameraGranted(true);
    } catch {
      setBlockedReason('camera');
      setStep('blocked');
      return;
    }

    // Location
    try {
      coords = await new Promise<GeolocationCoordinates>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          (pos) => resolve(pos.coords),
          (err) => reject(err),
          { enableHighAccuracy: true, timeout: 10000 }
        );
      });
      locGranted = true;
      setLocationGranted(true);
      setLocation(coords);
    } catch {
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
      setBlockedReason('location');
      setStep('blocked');
      return;
    }

    if (camGranted && locGranted) {
      // Start camera preview
      setStep('camera');
      setTimeout(() => {
        if (videoRef.current && streamRef.current) {
          videoRef.current.srcObject = streamRef.current;
          videoRef.current.play();
        }
      }, 100);
    }
  };

  const captureSelfie = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
    setCapturedSelfie(dataUrl);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
    }
  };

  const retakeSelfie = async () => {
    setCapturedSelfie(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' }, audio: false });
      streamRef.current = stream;
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play();
        }
      }, 100);
    } catch {
      setError('Não foi possível reabrir a câmera. Recarregue a página.');
    }
  };

  const confirmSelfie = async () => {
    if (!capturedSelfie || !location) return;
    setUploading(true);
    setError(null);

    try {
      // Convert base64 to blob
      const res = await fetch(capturedSelfie);
      const blob = await res.blob();
      const file = new File([blob], 'selfie-inicial.jpg', { type: 'image/jpeg' });

      const filePath = `registrations/${slug}/selfie-inicial-${Date.now()}.jpg`;

      // Upload using public URL (bucket is public, anon upload allowed)
      const { error: uploadError } = await supabase.storage
        .from('contract-files')
        .upload(filePath, file, { contentType: 'image/jpeg', upsert: true });

      if (uploadError) throw uploadError;

      const { data: publicUrlData } = supabase.storage
        .from('contract-files')
        .getPublicUrl(filePath);

      const selfieUrl = publicUrlData.publicUrl;
      const now = new Date().toISOString();

      // Update self registration record
      await supabase
        .from('client_self_registrations')
        .update({
          selfie_url: selfieUrl,
          latitude: location.latitude,
          longitude: location.longitude,
          location_accuracy: location.accuracy,
          location_captured_at: now,
          permission_camera: true,
          permission_location: true,
          device_info: navigator.userAgent,
          opened_at: now,
        })
        .eq('id', registrationId);

      onComplete({
        selfieUrl,
        latitude: location.latitude,
        longitude: location.longitude,
        locationAccuracy: location.accuracy,
        locationCapturedAt: now,
        permissionCamera: true,
        permissionLocation: true,
        deviceInfo: navigator.userAgent,
      });
    } catch (err) {
      console.error('Upload error:', err);
      setError('Erro ao salvar selfie. Tente novamente.');
    } finally {
      setUploading(false);
    }
  };

  if (step === 'blocked') {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] px-4 text-center">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
          <AlertCircle className="w-8 h-8 text-red-600" />
        </div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">Permissão Negada</h2>
        <p className="text-gray-600 mb-6">
          {blockedReason === 'camera'
            ? 'O cadastro requer acesso à câmera. Por favor, permita o acesso nas configurações do navegador e recarregue a página.'
            : 'O cadastro requer acesso à localização. Por favor, permita o acesso nas configurações do navegador e recarregue a página.'}
        </p>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-left max-w-sm w-full">
          <p className="text-sm font-semibold text-amber-800 mb-2">Como permitir:</p>
          <ol className="text-sm text-amber-700 space-y-1 list-decimal list-inside">
            <li>Toque no ícone de cadeado ou câmera na barra do navegador</li>
            <li>Permita o acesso à {blockedReason === 'camera' ? 'câmera' : 'localização'}</li>
            <li>Recarregue a página</li>
          </ol>
        </div>
        <button
          onClick={() => window.location.reload()}
          className="mt-6 w-full max-w-sm py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors"
        >
          Recarregar Página
        </button>
      </div>
    );
  }

  if (step === 'intro') {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Antes de começar</h2>
          <p className="text-gray-600">
            Para garantir a segurança do seu cadastro, precisamos das seguintes permissões:
          </p>
        </div>

        <div className="space-y-3">
          <div className="flex items-start gap-4 bg-blue-50 rounded-xl p-4">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Camera className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="font-semibold text-gray-900">Câmera</p>
              <p className="text-sm text-gray-600 mt-0.5">Para capturar sua selfie de identificação no início do cadastro.</p>
            </div>
          </div>

          <div className="flex items-start gap-4 bg-emerald-50 rounded-xl p-4">
            <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
              <MapPin className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="font-semibold text-gray-900">Localização</p>
              <p className="text-sm text-gray-600 mt-0.5">Para registrar o local onde o cadastro está sendo realizado.</p>
            </div>
          </div>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <p className="text-sm text-amber-800">
            <strong>Importante:</strong> O cadastro só pode ser realizado com câmera e localização ativas. Se qualquer permissão for negada, o processo será bloqueado.
          </p>
        </div>

        <button
          onClick={requestPermissions}
          className="w-full py-4 bg-blue-600 text-white rounded-xl font-semibold text-lg hover:bg-blue-700 active:bg-blue-800 transition-colors shadow-sm"
        >
          Permitir e Continuar
        </button>
      </div>
    );
  }

  if (step === 'requesting') {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] px-4 text-center">
        <Loader className="w-10 h-10 text-blue-500 animate-spin mb-4" />
        <p className="text-gray-700 font-medium">Solicitando permissões...</p>
        <p className="text-sm text-gray-500 mt-2">Aceite as solicitações do navegador para continuar.</p>
      </div>
    );
  }

  // Camera step
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-1">Tire sua selfie</h2>
        <p className="text-gray-600 text-sm">
          Posicione seu rosto dentro do quadro e clique em "Capturar".
        </p>
      </div>

      <div className="flex gap-3 text-xs">
        <span className="flex items-center gap-1 text-emerald-600">
          <CheckCircle className="w-3.5 h-3.5" /> Câmera ativa
        </span>
        <span className="flex items-center gap-1 text-emerald-600">
          <CheckCircle className="w-3.5 h-3.5" /> Localização capturada
        </span>
      </div>

      <div className="relative rounded-2xl overflow-hidden bg-black aspect-[3/4] max-h-[420px]">
        {!capturedSelfie ? (
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="w-full h-full object-cover scale-x-[-1]"
          />
        ) : (
          <img
            src={capturedSelfie}
            alt="Selfie capturada"
            className="w-full h-full object-cover scale-x-[-1]"
          />
        )}
        {/* Face guide overlay */}
        {!capturedSelfie && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-48 h-56 rounded-full border-2 border-white/70 border-dashed" />
          </div>
        )}
      </div>

      <canvas ref={canvasRef} className="hidden" />

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 rounded-lg text-red-700 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {!capturedSelfie ? (
        <button
          onClick={captureSelfie}
          className="w-full py-4 bg-blue-600 text-white rounded-xl font-semibold text-lg hover:bg-blue-700 active:bg-blue-800 transition-colors shadow-sm"
        >
          <Camera className="w-5 h-5 inline mr-2" />
          Capturar Selfie
        </button>
      ) : (
        <div className="space-y-3">
          <button
            onClick={confirmSelfie}
            disabled={uploading}
            className="w-full py-4 bg-emerald-600 text-white rounded-xl font-semibold text-lg hover:bg-emerald-700 transition-colors shadow-sm disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {uploading ? (
              <><Loader className="w-5 h-5 animate-spin" /> Salvando...</>
            ) : (
              <><CheckCircle className="w-5 h-5" /> Usar esta foto</>
            )}
          </button>
          <button
            onClick={retakeSelfie}
            disabled={uploading}
            className="w-full py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors"
          >
            Tirar novamente
          </button>
        </div>
      )}
    </div>
  );
}
